import "./Tooltip.scss";

import { type FC, type ReactNode, type Ref, useId } from "react";
import { ITooltip, Tooltip as ReactTooltip, TooltipRefProps } from "react-tooltip";

import { CircleInfoIcon } from "mds/icons";

export type TooltipTriggerProps = {
  ref?: Ref<TooltipRefProps>;
  content: ReactNode;
  children?: ReactNode;
  label?: string;
} & Omit<ITooltip, "id" | "content" | "children">;

/**
 * Tooltip for non-focusable triggers (icons, text). Always wraps children in an
 * unstyled button so the tooltip opens on keyboard focus.
 * Always provide a label if the trigger is an icon.
 * Use {@link Tooltip} when wrapping an already-focusable control (button, link, etc.)
 */
export const TooltipTrigger: FC<TooltipTriggerProps> = ({
  ref,
  content,
  children,
  label,
  globalCloseEvents,
  ...props
}) => {
  const id = `tooltip-trigger-${useId()}`;
  const triggerContent = children ?? <CircleInfoIcon height="16" width="16" />;

  if (!content) {
    return triggerContent;
  }

  return (
    <div className="mds-tooltip flex">
      <button
        aria-describedby={id}
        aria-label={label}
        className="inline-flex cursor-pointer appearance-none items-center justify-center border-0 bg-transparent p-0 font-[inherit] text-[inherit] outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-1"
        data-tooltip-delay-hide={0}
        data-tooltip-delay-show={0}
        data-tooltip-id={id}
        data-tooltip-variant="light"
        type="button"
      >
        {triggerContent}
      </button>
      <ReactTooltip
        className="mds-tooltip-content"
        id={id}
        offset={8}
        {...props}
        globalCloseEvents={{ ...globalCloseEvents, escape: true }}
        ref={ref}
        clickable
        disableStyleInjection
        noArrow
      >
        {content}
      </ReactTooltip>
    </div>
  );
};

TooltipTrigger.displayName = "TooltipTrigger";
